import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { Form, Formik } from "formik";
import { DrawerForm } from "../Common/styles/drawer";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { ModalActionButtonStyles } from "../Common/styles/modal";

type Props = {
  closeDrawer: () => void;
  refreshPage: () => void;
  data: any;
};

let schema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Room Name is Required")
    .max(100, "Room Name cannot be greater than 100 Characters"),
  siteId: yup.string().required("Location is Required"),
});

const AddRoomDrawer = ({ closeDrawer, refreshPage, data }: Props) => {
  const [siteList, setSiteList] = useState<any[]>([]);

  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(`/registry/sites`);
        const newData = res?.data?.data?.map((country: any) => ({
          value: country?.id,
          label: country?.name,
        }));
        setSiteList(newData);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        name: values?.name,
        siteId: values?.siteId,
      };
      let res: AxiosResponse;

      if (data?.id) {
        res = await http.patch(
          `/admin/registry/appointment-rooms/${data?.id}`,
          body
        );
      } else {
        res = await http.post(`/admin/registry/appointment-rooms`, body);
      }

      toastMessage("success", res?.data?.message);
      closeDrawer();
      refreshPage();
      setSubmitLoader(false);
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };
  return (
    <Box
      sx={{
        width: "900px",
        p: 2.5,
        height: "100%",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "25px",
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
          Add a New Location
        </Typography>
        <IconButton onClick={closeDrawer}>
          <CloseRounded sx={{ color: "text.secondary" }} />
        </IconButton>
      </Box>
      <Divider sx={{ mt: 2, mb: 3 }} />
      <Box
        sx={{
          width: "100%",
          height: "calc(100% - 66px)",
          overflow: "auto",
        }}
      >
        {!loading ? (
          <Formik
            initialValues={{
              name: data?.name || "",
              siteId: data?.siteId || "",
            }}
            validationSchema={schema}
            onSubmit={(values) => {
              submitHandler(values);
            }}
          >
            {({ getFieldProps, errors, touched }) => (
              <Form style={DrawerForm}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="room-name">
                    Room Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Room name"
                    id="room-name"
                    inputProps={{
                      maxLength: 100,
                    }}
                    {...getFieldProps("name")}
                    error={touched?.name && errors?.name ? true : false}
                    helperText={
                      touched?.name && errors?.name
                        ? (errors?.name as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="site-id">
                    Location <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select id="siteId" {...getFieldProps("siteId")}>
                    {siteList.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={touched?.siteId && errors?.siteId ? true : false}
                  >
                    {touched?.siteId && errors?.siteId
                      ? (errors?.siteId as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <Box sx={{ ...ModalActionButtonStyles, mt: "auto" }}>
                  {!submitLoader ? (
                    <Button type="submit" variant="contained">
                      {data?.id ? "Update" : "Create"}
                    </Button>
                  ) : (
                    <Box sx={{ p: 1 }}>
                      <CircularProgress size={25} />
                    </Box>
                  )}
                </Box>
              </Form>
            )}
          </Formik>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <CircularProgress size={25} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AddRoomDrawer;
